import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';

/**
 * Request a password reset email. POSTs to /auth/forgot-password which
 * signs a short-lived JWT and emails a link to /reset-password?token=…
 * Response is intentionally the same whether or not the email is
 * registered — don't leak which accounts exist.
 */
const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-cream min-h-[60vh]">
      <Helmet>
        <title>Forgot your password — EJL Education</title>
      </Helmet>
      <section className="section">
        <div className="container-content max-w-[48ch]">
          <h1 className="mb-4">{submitted ? 'Check your inbox' : 'Forgot your password?'}</h1>
          {submitted ? (
            <>
              <p className="text-body text-ink/90 mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a link to set a new password. It'll expire in an hour.
              </p>
              <Link to="/login" className="btn btn-primary">Back to login</Link>
            </>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-6">
              <p className="text-body text-ink/90">
                Enter the email on your account and we'll send a reset link.
              </p>
              {error && (
                <div className="border border-rule bg-paper p-4">
                  <p className="text-ink text-[15px]">{error}</p>
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-[15px] font-medium text-ink mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="flex justify-between items-center pt-2">
                <Link to="/login" className="text-sm text-muted hover:text-ochre">
                  Back to login
                </Link>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {busy ? 'Sending…' : 'Send reset link'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default ForgotPasswordPage;
